import React, { useState } from "react";
import { Bet, useUpdateBetResultMutation } from "@/api/betting";
import { useToast } from "@/hooks/use-toast";
import { PersonaAvatar } from "@/components/PersonaAvatar";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "./utils/formatHelpers";
import { getStatusBadge, getValidationBadge } from "./StatusBadges";
import BetDetailsModal from "./modals/BetDetailsModal";
import UpdateResultModal from "./modals/UpdateResultModal";
import type { Persona } from "@/api/personas";

interface BetCardProps {
  bet: Bet;
  personaId?: string;
  personas: Persona[];
}

const BetCard: React.FC<BetCardProps> = ({ bet, personaId, personas }) => {
  const { toast } = useToast();
  const updateBetResult = useUpdateBetResultMutation();
  const [showResultModal, setShowResultModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [resultAction, setResultAction] = useState<"won" | "lost">("won");

  // Use bet's own persona_id if no personaId was provided (in "All" view)
  const effectivePersonaId = personaId || bet.persona_id;

  // Find the persona data
  const persona = personas.find((p) => p.persona_id === bet.persona_id);
  const personaName = persona ? persona.name : "Unknown Agent";

  // Extract event info from validation result if available
  const eventInfo = bet.validation_result?.event_info;
  const league = eventInfo?.league;
  const eventDate = eventInfo?.event_date;

  // Get enhanced game info if available from validation
  const enhancedGame = bet.validation_result?.bet_updates?.game || bet.game;

  // Extract sources if available
  const sources = bet.validation_result?.sources || [];
  const hasValidSources = sources.length > 0;

  const handleUpdateResult = (status: "won" | "lost") => {
    if (!effectivePersonaId) {
      toast({
        variant: "destructive",
        description: "Cannot update bet: No persona ID found",
      });
      return;
    }

    // Show result modal
    setResultAction(status);
    setShowResultModal(true);
  };

  // Function to mark bet with specific result
  const markBetAs = (status: string, resultText: string = "") => {
    updateBetResult.mutate(
      {
        persona_id: effectivePersonaId,
        bet_id: bet.id,
        status,
        result: {
          updated_by: "user",
          updated_at: new Date().toISOString(),
          result_text: resultText || undefined,
        },
      },
      {
        onSuccess: () => {
          toast({
            description: `Bet marked as ${status}`,
          });
          setShowResultModal(false);
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            description: `Failed to update bet result: ${error.message}`,
          });
        },
      },
    );
  };

  // Extract result text from bet.result if it exists
  const existingResult = bet.result?.result_text || "";

  // Get outcome info from validation result if available
  const outcomeInfo = bet.validation_result?.outcome;
  const outcomeScore = outcomeInfo?.score;

  return (
    <div className="bg-black/40 border-zinc-800 border rounded-xl overflow-hidden">
      {/* Header with persona info and status */}
      <div className="flex justify-between items-center px-3 py-2 bg-black/60">
        <div className="flex items-center gap-1.5">
          {persona && <PersonaAvatar persona={persona} className="w-5 h-5" />}
          <span className="font-medium text-sm truncate max-w-24">
            {personaName}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {getStatusBadge(bet.status)}
          {getValidationBadge(bet.validation_status)}
        </div>
      </div>

      {/* Sport and game */}
      <div className="px-3 pt-2">
        <div className="font-semibold text-sm">{bet.sport}</div>
        <div className="text-xs text-muted-foreground truncate mb-1">
          {enhancedGame}
        </div>

        {/* Enhanced event info from validation - if available */}
        {(league || eventDate) && (
          <div className="flex flex-wrap items-center gap-x-2 mt-1 mb-1.5">
            {league && (
              <Badge
                variant="outline"
                className="text-[10px] h-4 px-1 py-0 border-zinc-700 text-zinc-400"
              >
                {league}
              </Badge>
            )}
            {eventDate && (
              <Badge
                variant="outline"
                className="text-[10px] h-4 px-1 py-0 border-zinc-700 text-zinc-400"
              >
                {eventDate}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Prediction */}
      <div className="px-3">
        <div className="text-xs font-medium">{bet.prediction}</div>
      </div>

      {/* Source badge - if validated with sources */}
      {hasValidSources && (
        <div className="px-3 mt-1.5 mb-1">
          <Badge
            className="bg-green-600/20 hover:bg-green-600/30 text-green-500 border-green-800 text-[10px] cursor-pointer"
            onClick={() => setShowDetailsModal(true)}
          >
            Verified with {sources.length}{" "}
            {sources.length === 1 ? "source" : "sources"}
          </Badge>
        </div>
      )}

      {/* Result display - if bet has a result */}
      {bet.status !== "pending" && (existingResult || outcomeScore) && (
        <div className="px-3 py-1 mt-1">
          <div className="text-xs font-medium">Result:</div>
          <div className="text-xs bg-black/20 p-1.5 rounded text-white">
            {existingResult || outcomeScore}
          </div>
        </div>
      )}

      {/* Odds & date */}
      <div className="flex justify-between items-center px-3 py-1 text-xs text-muted-foreground mt-0.5">
        <div className="flex items-center gap-1.5">
          <span>Odds:</span>
          <span className="text-white">{bet.odds}</span>
        </div>
        <div>{formatDate(bet.created_at)}</div>
      </div>

      {/* Action buttons */}
      <div className="flex border-t border-zinc-800 divide-x divide-zinc-800">
        {bet.status === "pending" ? (
          <>
            <button
              className="flex-1 py-1.5 text-xs font-medium text-center text-green-500 hover:bg-green-500/10 transition"
              onClick={() => handleUpdateResult("won")}
            >
              WON
            </button>
            <button
              className="flex-1 py-1.5 text-xs font-medium text-center text-red-500 hover:bg-red-500/10 transition"
              onClick={() => handleUpdateResult("lost")}
            >
              LOST
            </button>
          </>
        ) : (
          <button
            className="flex-1 py-1.5 text-xs font-medium text-center text-blue-500 hover:bg-blue-500/10 transition"
            onClick={() => setShowDetailsModal(true)}
          >
            VIEW DETAILS
          </button>
        )}
      </div>

      {/* Modals */}
      <UpdateResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        onSubmit={(resultText) => markBetAs(resultAction, resultText)}
        status={resultAction}
        bet={bet}
      />

      <BetDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        bet={bet}
        persona={persona}
        onMarkAsWon={() => {
          setShowDetailsModal(false);
          setTimeout(() => handleUpdateResult("won"), 100);
        }}
        onMarkAsLost={() => {
          setShowDetailsModal(false);
          setTimeout(() => handleUpdateResult("lost"), 100);
        }}
      />
    </div>
  );
};

export default BetCard;
